import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FiAlertCircle, FiClock } from "react-icons/fi";
import { ModalPortal } from "../../ModalPortal";

export interface CustomAlertModalProps {
  visible?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  text?: React.ReactNode;
  onDonePress?: () => void;
  onCancelPress?: () => void;
  doneText?: string;
  cancelText?: string;
  onSingleButton?: boolean;
  isError?: boolean;
}

export const CustomAlertModal: React.FC<CustomAlertModalProps> = ({
  visible,
  isOpen,
  onClose,
  text,
  onDonePress,
  onCancelPress,
  doneText,
  cancelText,
  onSingleButton = false,
  isError = false,
}) => {
  const { t } = useTranslation();
  const show = visible !== undefined ? visible : isOpen;
  const resolvedDoneText = doneText ?? t("select");

  const onDone = () => {
    onDonePress?.();
    onClose?.();
  };

  const onCancel = () => {
    onCancelPress?.();
    onClose?.();
  };

  return (
    <ModalPortal>
    <AnimatePresence>
      {show && (
        <motion.div
          key="custom-alert-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            key="custom-alert-card"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className={`bg-white rounded-3xl shadow-2xl w-full max-w-[500px] p-6 sm:p-7 flex flex-col items-center text-center gap-4 ${
              isError ? "border-2 border-red-500" : "border border-gray-100"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center ${
                isError
                  ? "bg-red-50 text-red-500"
                  : "bg-[#020637]/5 text-[#020637]"
              }`}
            >
              {isError ? (
                <FiAlertCircle className="w-7 h-7" />
              ) : (
                <FiClock className="w-7 h-7" />
              )}
            </div>

            <p className="text-gray-800 text-base sm:text-lg font-medium leading-relaxed whitespace-pre-line px-2">
              {text}
            </p>

            <div className="flex items-center gap-3 w-full mt-2">
              {!onSingleButton && (
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onCancel}
                  className="flex-1 py-3.5 px-3 sm:px-4 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold text-xs sm:text-sm transition-all whitespace-nowrap text-center flex items-center justify-center"
                >
                  {cancelText ?? t("cancel")}
                </motion.button>
              )}
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onDone}
                className={`${
                  onSingleButton ? "w-full" : "flex-1"
                } py-3.5 px-3 sm:px-4 rounded-xl bg-gradient-to-r from-blue-600 to-[#020637] hover:from-blue-700 hover:to-[#020637] text-white font-semibold text-xs sm:text-sm shadow-md hover:shadow-lg transition-all whitespace-nowrap text-center flex items-center justify-center`}
              >
                {resolvedDoneText}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </ModalPortal>
  );
};

export default CustomAlertModal;
